import { gql, useQuery } from '@apollo/client'
import { useAuth0 } from '@auth0/auth0-react'
import 'boxicons/css/boxicons.min.css'
import { Banner, Footer, Navbar, NavbarCardsProvider } from 'component-library'
import CoachMarkGatsbyRoot from 'component-library/gatsby'
import { ReactNode, useEffect, useState } from 'react'
import { BannerContent, FooterContent, NavbarContent } from 'shared-content'
import { useGAUserTracking } from '../../hooks/useGAUserTracking'
import { useNavbarCardsQuery } from '../../hooks/useNavbarCardsQuery'
import { usePathPrefix } from '../../hooks/usePathPrefix'
import { useReleaseMenuCardQuery } from '../../hooks/useReleaseMenuCardQuery'
import LinkComponent from '../LinkComponent/LinkComponent'
import MarkdownText from '../MarkdownText/MarkdownText'

const GET_USER_HANDLE = gql`
  query GetUserHandle {
    me {
      handle
    }
  }
`

interface ILayoutProps {
  children?: ReactNode
}

const Layout = ({ children }: ILayoutProps) => {
  const pathPrefix: string = usePathPrefix()
  const { isAuthenticated, loginWithRedirect, logout } = useAuth0()
  const [userHandle, setUserHandle] = useState<string>()

  // Track user_id in Google Analytics
  useGAUserTracking()

  const { data, error } = useQuery(GET_USER_HANDLE)

  useEffect(() => {
    const handle = data?.me?.handle || ''
    if (handle) setUserHandle(handle)
    if (error) console.error(error)
  }, [data, error])

  useEffect(() => {
    const getUpdatedUserHandle = () => {
      setUserHandle(sessionStorage.getItem('userHandle') || userHandle)
    }
    window.addEventListener('userHandleUpdate', getUpdatedUserHandle)
    return () =>
      window.removeEventListener('userHandleUpdate', getUpdatedUserHandle)
  }, [])

  return (
    <>
      <NavbarCardsProvider
        useCardsQuery={useNavbarCardsQuery}
        useReleaseQuery={useReleaseMenuCardQuery}
      >
        <Navbar
          isAuthenticated={isAuthenticated}
          leftItems={NavbarContent.navbarLeft}
          rightItems={NavbarContent.navbarRight}
          signInCard={NavbarContent.signInCard}
          mobileButton={NavbarContent.mobileButton}
          LinkComponent={LinkComponent}
          MarkdownText={MarkdownText}
          handle={userHandle}
          createAccount={() => {
            loginWithRedirect({
              authorizationParams: { screen_hint: 'signup' },
            })
          }}
          logIn={() => {
            loginWithRedirect({
              appState: { returnTo: window.location.pathname },
            })
          }}
          signOut={() => {
            logout({
              logoutParams: {
                returnTo:
                  typeof window !== 'undefined'
                    ? `${window.location.origin}${pathPrefix}/`
                    : '',
              },
            })
          }}
        />
      </NavbarCardsProvider>
      {BannerContent.bannerText.length > 0 && (
        <Banner bannerText={<MarkdownText text={BannerContent.bannerText} />} />
      )}
      {children}
      <CoachMarkGatsbyRoot />
      <Footer
        footerDescription={
          <MarkdownText text={FooterContent.footerAbout.description} />
        }
        footerCreatedBy={
          <MarkdownText
            text={FooterContent.footerAbout.createdBy}
            gaEventCategory="footer_ux"
            gaEventLabel="footer_xanadu_click"
            gaEventValue="redirect to xanadu homepage"
          />
        }
        {...FooterContent}
        LinkComponent={LinkComponent}
      />
    </>
  )
}

export default Layout
